import React, {
  MutableRefObject,
  ReactElement,
  type ReactNode,
  Ref,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { Input as AntInput } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { ValidationContext } from './Validation';
import { scrollbar } from '../../styles/GlobalStyle';
import { TextAreaRef } from 'antd/es/input/TextArea';

const { TextArea: AntTextArea } = AntInput;

type TextAreaValueType = PlainText | string;

type ShowCountSettings =
  | {
      formatter: (info: ShowCountInfo) => ReactNode;
    }
  | boolean;

interface ShowCountInfo {
  value: string;
  count: number;
  maxLength?: number;
}

interface AutosizeSettings {
  minRows?: number;
  maxRows?: number;
}

interface CustomStyles {
  height?: CSSSize | 0;
  resize?: 'vertical';
  verticalAlign?: 'text-top';
  disableCursor?: 'pointer';
  disableBackgroundColor?: Color;
  placeholderSize?: CSSSize;
  fontSize?: CSSSize;
  fontFamily?: string;
}

interface TextAreaProps<ValueType> {
  value?: ValueType;
  onChange?: (value: ValueType) => void;
  onFocus?: () => void;
  autoFocus?: boolean;
  onCaretPositionChanged?: (value: number) => void;
  onPressEnter?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  rows?: number;
  autoSize?: AutosizeSettings | boolean;
  initialSingleRow?: boolean;
  className?: string;
  disabled?: boolean;
  showCount?: (value: ValueType) => React.ReactNode | boolean;
  maxLength?: number;
  customStyles?: CustomStyles;
}

function TextArea<ValueType extends TextAreaValueType>({
  value,
  onChange,
  placeholder,
  className,
  rows,
  autoSize,
  disabled,
  showCount,
  maxLength,
  customStyles,
  onCaretPositionChanged,
  onPressEnter,
  autoFocus = false,
  onFocus,
}: TextAreaProps<ValueType>): ReactElement {
  const { validateStatus } = useContext(ValidationContext);
  const textAreaRef = useRef<TextAreaRef>();

  useEffect(() => {
    if (autoFocus && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [autoFocus]);

  const antOnChange: React.ChangeEventHandler<HTMLTextAreaElement> = e => {
    onChange && onChange(e.target.value as ValueType);
  };

  const antShowCount = (): ShowCountSettings => {
    if (typeof showCount === 'function') {
      return { formatter: (info: ShowCountInfo) => showCount(info.value as ValueType) };
    }

    return showCount || false;
  };

  const changeCaretPosition = () => {
    onCaretPositionChanged &&
      onCaretPositionChanged(textAreaRef.current?.resizableTextArea?.textArea.selectionStart || 0);
  };

  return (
    <StyledTextArea
      ref={textAreaRef as Ref<TextAreaRef>}
      value={value}
      customstyles={customStyles}
      style={{ resize: customStyles?.resize }}
      showCount={antShowCount()}
      onChange={antOnChange}
      onFocus={onFocus}
      maxLength={maxLength}
      disabled={disabled}
      rows={rows}
      autoSize={autoSize}
      placeholder={placeholder}
      className={className}
      status={validateStatus}
      onInput={changeCaretPosition}
      onClick={changeCaretPosition}
      onContextMenu={changeCaretPosition}
      onPressEnter={e => onPressEnter && onPressEnter(e)}
      onKeyUp={({ key }) => {
        if (['Arrow', 'Page', 'Home', 'End'].some(type => key.startsWith(type))) {
          changeCaretPosition();
        }
      }}
    />
  );
}

const StyledTextArea = styled(AntTextArea)<{ customstyles?: CustomStyles }>`
  &.ant-input {
    font-size: ${props => props.customstyles?.fontSize ?? '16px'};
    font-family: ${props => props.customstyles?.fontFamily};
    height: ${props => props.customstyles?.height};
    vertical-align: ${props => props.customstyles?.verticalAlign};
  }

  ${scrollbar};

  &.ant-input:focus {
    border-color: ${colorsPalette.dustyBlue500};
  }

  &.ant-input:hover {
    border-color: ${colorsPalette.dustyBlue500};
  }

  &.ant-input-affix-wrapper-disabled {
    cursor: ${props => props.customstyles?.disableCursor};
    background-color: ${props => props.customstyles?.disableBackgroundColor};
  }

  .ant-input::placeholder {
    font-size: ${props => props.customstyles?.placeholderSize};
  }
`;

export default TextArea;
